import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../auth/AuthContext";
import { logo } from "../config";

const inputCls = "w-full bg-ink border border-white/10 rounded-lg px-4 py-3 font-body text-sm text-white placeholder:text-silver/30 focus:border-gold/50 focus:outline-none transition-colors";

function Shell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grain bg-ink text-white flex flex-col">
      <div className="px-6 md:px-10 lg:px-16 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-silver/60 hover:text-gold transition-colors text-sm font-body" data-testid="back-home">
          <ArrowLeft size={16} className="rtl-flip" /> Back to site
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="flex flex-col items-center text-center mb-8">
            <img src={logo} alt="Arco Orbis2" className="h-16 w-16 object-contain" />
            <h1 className="mt-5 font-display font-light text-3xl">{title}</h1>
            <p className="mt-2 text-silver/60 font-body font-light text-sm">{subtitle}</p>
          </div>
          <div className="hairline rounded-2xl bg-ink-surface/60 p-7">{children}</div>
          <p className="mt-6 text-center text-sm font-body text-silver/50">{footer}</p>
        </motion.div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title={<>Client <span className="gold-text">Login</span></>} subtitle="Access your Arco Orbis2 account"
      footer={<>No account? <Link to="/register" className="text-gold hover:underline">Create one</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <span className="overline text-silver/50 text-[0.6rem] mb-2 inline-block">Email</span>
          <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email" />
        </div>
        <div>
          <span className="overline text-silver/50 text-[0.6rem] mb-2 inline-block">Password</span>
          <input type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" />
        </div>
        {error && <p className="text-sm text-red-400 font-body" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={loading} className="gold-sweep w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black px-6 py-3.5 text-sm font-body font-medium hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="login-submit">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} Sign In
        </button>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title={<>Create <span className="gold-text">Account</span></>} subtitle="Join Arco Orbis2"
      footer={<>Already have an account? <Link to="/login" className="text-gold hover:underline">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <span className="overline text-silver/50 text-[0.6rem] mb-2 inline-block">Name</span>
          <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="register-name" />
        </div>
        <div>
          <span className="overline text-silver/50 text-[0.6rem] mb-2 inline-block">Email</span>
          <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="register-email" />
        </div>
        <div>
          <span className="overline text-silver/50 text-[0.6rem] mb-2 inline-block">Password</span>
          <input type="password" className={inputCls} value={form.password} onChange={set("password")} placeholder="At least 6 characters" data-testid="register-password" />
        </div>
        {error && <p className="text-sm text-red-400 font-body" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={loading} className="gold-sweep w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black px-6 py-3.5 text-sm font-body font-medium hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="register-submit">
          {loading ? <Loader2 size={18} className="animate-spin" /> : null} Create Account
        </button>
      </form>
    </Shell>
  );
}
